import { DOMUtil, StringUtil } from "@mcleod/core";
import { Component, Layout, MainPageAccessor, ScreenStack } from "@mcleod/components";
import { StickyComponent } from "./StickyComponent";

enum State {
    UNSTUCK = 'unstuck',
    STUCK_HIDDEN = 'hidden',
    STUCK_VISIBLE = 'stuck'
}

interface StickyElementState {
    element: HTMLElement;
    contentComponent: Component;
    currentState?: State;
    placeholder?: HTMLElement;
    unStickPos? : number;
    originalStyles?: {
        position: string;
        top: string;
        zIndex: string;
        backgroundColor: string;
        width: string;
        borderRadius?: string;
        borderTopLeftRadius?: string;
        borderTopRightRadius?: string;
        borderBottomLeftRadius?: string;
        borderBottomRightRadius?: string;
    };
    doOnStick?: (currentStickyTop?: number) => void;
    doOnUnstick?: (currentStickyTop?: number) => void;
}

export class AppStickyManager {
    private readonly layout: Layout;
    private scrollableElement: HTMLElement;
    private stickyComponents: Map<HTMLElement, StickyElementState> = new Map();
    private currentStickyTop: number;
    private currentStickyZIndex: number;
    private readonly stickyScrollListener: EventListener;
    private _lastScrollTop: number = 0;
    private _lastScrollLeft: number;
    private _scrollUpBuffer: number = 0;

    public static manageLayout(layout: Layout) {
        new AppStickyManager(layout);
    }

    private constructor(layout: Layout) {
        this.layout = layout;

        // FIXME This isn't the right place for setting this height to auto and I would like a more elegant solution.
        //  Setting this value, along with AppPanelStyles' variant of fillRowHeight allow for a single scrollable area
        //  when viewing layouts.
        this.layout.height = 'auto';

        this.stickyScrollListener = (event: Event) => this.handleStickyScrollEvent(event);
        this.layout.addLayoutLoadListener(() => {
            this.layout.addMountListener(() => {
                this.scrollableElement = DOMUtil.findScrollableParent(layout._element) || layout._element.parentElement;
                this.scrollableElement?.addEventListener('scroll', this.stickyScrollListener, { capture: true });
            });

            this.layout.addUnmountListener(() => {
                this.scrollableElement?.removeEventListener('scroll', this.stickyScrollListener);
            });
        });

        this.registerLayout(this.layout);
    }

    public registerLayout(layout: Layout) {
        for (const component of layout.getRecursiveChildren()) {
            if (component instanceof Layout && component.isNested) {
                continue;
            }
            component["stickyComponents"]?.forEach((sticky: StickyComponent) => {
                this.addStickyComponent(sticky);
            });
        }
    }

    public addStickyComponent(sticky: StickyComponent): void {
        this.stickyComponents.set(sticky.element, {
            element: sticky.element,
            contentComponent: sticky.contentComponent,
            doOnStick: sticky.doOnStick,
            doOnUnstick: sticky.doOnUnstick
        });
        sticky.onRegistration?.(this);
    }

    private handleStickyScrollEvent(event: Event): void {
        if (!this.isVerticalScrolling(event)) {
            return; // Ignore horizontal scroll events
        }

        this.currentStickyTop ??= MainPageAccessor.router?._element?.getBoundingClientRect()?.top || 0;
        // Much like ScreenStack does, I picked a number that is higher than the default zIndex of 45 that
        // is set in that class, in case ScreenStack is undefined, which is the situation where a developer
        // reloads the screen during development.
        this.currentStickyZIndex ??= ScreenStack.getHighestZIndex() + 1 || 46;
        const scrollTarget = event.target as HTMLElement;
        const scrollTop = scrollTarget.scrollTop;

        // Determine scroll direction
        const isScrollingUp = scrollTop < this._lastScrollTop;
        this._lastScrollTop = scrollTop;

        this.stickyComponents.forEach((state: StickyElementState) => {
            // Store original styles on the first run
            this.setOriginalStyles(state);

            if (this.shouldEvaluateStickyComponent(state)) {
                const contentInView = this.isContentPartiallyInView(state, isScrollingUp);

                if (isScrollingUp) {
                    // if we are scrolling up, then we plan to either make hidden stickies visible or unstick stuck components.
                    if (!state.unStickPos || state.unStickPos >= scrollTop) {
                        this.unstickComponent(state);
                    } else if (state.currentState === State.STUCK_HIDDEN && contentInView) {
                        this.showHiddenStickiedComponent(state);
                    }
                } else {
                    // if we are scrolling down, then we need to either make stickied components hidden or stick components.
                    if (state.placeholder === undefined && state.element.getBoundingClientRect().top <= this.currentStickyTop && contentInView) {
                        if (state.element.parentNode !== null)
                            this.stickComponent(state, scrollTop);
                    } else if (state.currentState === State.STUCK_VISIBLE && !contentInView) {
                        this.hideStickiedComponent(state);
                    }
                }
            }
        });
    }

    private isVerticalScrolling(event: Event) {
        // Check if the scroll is horizontal by comparing previous and current scrollLeft values
        const scrollTarget = event.target as HTMLElement;

        // // Store scrollLeft in a class variable if not already tracking it
        this._lastScrollLeft ??= scrollTarget.scrollLeft;

        // If scrollLeft has changed, this is a horizontal scroll - ignore it
        if (this._lastScrollLeft !== scrollTarget.scrollLeft) {
            this._lastScrollLeft = scrollTarget.scrollLeft;
            return false ; // Return false for horizontal scrolling
        }

        if (this._lastScrollTop === scrollTarget.scrollTop) {
            return false;
        }

        // Update scrollLeft for next comparison
        this._lastScrollLeft = scrollTarget.scrollLeft;
        return true; // Return true for vertical scrolling
    }

    private shouldEvaluateStickyComponent(state: StickyElementState): boolean {
        // Add other situations where we should not evaluate here.
        return state.element['_mcleodComponent']?.visible !== false;
    }

    private isContentPartiallyInView(state: StickyElementState, scrollingUp: boolean = false): boolean {
        if (!state) return false;

        const contentRect = state.contentComponent._element.getBoundingClientRect();
        // Using the placeholder's height appears to be more reliable, than the actual component itself.
        const stickyComponentHeight: number = DOMUtil.getStyleAttrAsNumber(state.placeholder?.style?.height) || 0;

        const buffer = (scrollingUp) ? this._scrollUpBuffer : 0;
        const viewableTop = this.currentStickyTop - stickyComponentHeight - buffer;
        return (contentRect.bottom > viewableTop);
    }

    private stickComponent(state: StickyElementState, scrollPos: number): void {
        if (state.element.parentNode == null)
            return;

        state.currentState = State.STUCK_VISIBLE;
        const element = state.element;

        // Create a placeholder before changing position to fixed
        const componentRect = element.getBoundingClientRect();
        const placeholder = document.createElement('div');
        placeholder.style.height = `${componentRect.height}px`;
        placeholder.style.width = `${componentRect.width}px`;
        placeholder.style.margin = '0';
        placeholder.style.padding = '0';
        placeholder.style.visibility = 'hidden';

        // Insert a placeholder before changing position
        element.parentNode.insertBefore(placeholder, element);
        state.placeholder = placeholder;
        state.unStickPos = scrollPos;

        if (StringUtil.isEmptyString(element.style.backgroundColor) || element.style.backgroundColor === "transparent") {
            const computedStyle = window.getComputedStyle(element);
            const computedBackgroundColor = computedStyle.backgroundColor;
            if (!StringUtil.isEmptyString(computedBackgroundColor) && computedBackgroundColor.indexOf('0, 0, 0, 0') === -1)
                element.style.backgroundColor = computedBackgroundColor;
            else
                element.style.backgroundColor = "white";
        }
        element.style.width = `calc(100% - ${this.scrollbarWidth}px)`;
        element.style.borderRadius = "0";
        element.style.zIndex = `${this.currentStickyZIndex + 1}`;
        element.style.borderTopLeftRadius = "0";
        element.style.borderTopRightRadius = "0";
        element.style.borderBottomLeftRadius = "0";
        element.style.borderBottomRightRadius = "0";

        this.manageStickyComponentTops();
        state.doOnStick?.();
    }

    private unstickComponent(state: StickyElementState): void {
        if (state.currentState === State.STUCK_HIDDEN)
            this.showHiddenStickiedComponent(state);

        state.currentState = State.UNSTUCK;
        const element = state.element;

        // Remove the placeholder when unsticking
        if (state.placeholder && state.placeholder.parentNode) {
            state.placeholder.parentNode.removeChild(state.placeholder);
            state.placeholder = undefined;
        }
        state.unStickPos = undefined;

        element.style.position = state.originalStyles.position;
        element.style.top = state.originalStyles.top;
        element.style.zIndex = state.originalStyles.zIndex;
        element.style.backgroundColor = state.originalStyles.backgroundColor;
        element.style.width = state.originalStyles.width;
        element.style.borderRadius = state.originalStyles.borderRadius;
        element.style.borderTopLeftRadius = state.originalStyles.borderTopLeftRadius;
        element.style.borderTopRightRadius = state.originalStyles.borderTopRightRadius;
        element.style.borderBottomLeftRadius = state.originalStyles.borderBottomLeftRadius;
        element.style.borderBottomRightRadius = state.originalStyles.borderBottomRightRadius;

        this.manageStickyComponentTops();
        state.doOnUnstick?.();
    }

    unstickAllComponents(): void {
        this.stickyComponents.forEach((state: StickyElementState) => {
            if (state.currentState === State.STUCK_HIDDEN) {
                this.showHiddenStickiedComponent(state);
            }
            if (state.currentState === State.STUCK_VISIBLE) {
                this.unstickComponent(state);
            }
        });
    }

    unstickByElement(element: HTMLElement): void {
        const state = this.stickyComponents.get(element);
        if (state == null || state.currentState == null || state.currentState === State.UNSTUCK) {
            return;
        }
        if (state.currentState === State.STUCK_HIDDEN) {
            this.showHiddenStickiedComponent(state);
        }
        this.unstickComponent(state);
    }

    private hideStickiedComponent(state: StickyElementState) {
        // When the state's content has scrolled above routerViewableTop, leave the component stickied but hide the component through CSS Styling so that it takes up no vertical space.
        state.currentState = State.STUCK_HIDDEN;
        state.element.style.position = "fixed";
        state.element.style.top = `${this.currentStickyTop}px`;
        state.element.style.zIndex = `${this.currentStickyZIndex}`;
        state.element.style.visibility = "hidden";
        state.element.style.opacity = "0";
        state.element.style.pointerEvents = "none";

        this.manageStickyComponentTops();
    }

    private showHiddenStickiedComponent(state: StickyElementState) {
        // When the state's content has scrolled into view, below routerViewableTop, undo what was done in the method hideStickiedComponent
        state.currentState = State.STUCK_VISIBLE;
        state.element.style.position = "fixed";
        state.element.style.width = `calc(100% - ${this.scrollbarWidth}px)`;
        state.element.style.top = `${this.currentStickyTop}px`;
        state.element.style.zIndex = `${this.currentStickyZIndex}`;
        state.element.style.visibility = "visible";
        state.element.style.opacity = "1";
        state.element.style.pointerEvents = "auto";

        this.manageStickyComponentTops();
    }

    private manageStickyComponentTops(): void {
        let tempStickyTop = MainPageAccessor.router?._element?.getBoundingClientRect()?.top || 0;
        this.stickyComponents.forEach(state => {
            // set each component's top value if it is sticky so that they cascade in natural order
            if (state.currentState === State.STUCK_VISIBLE || state.currentState === State.STUCK_HIDDEN) {
                const element = state.element;

                element.style.position = "fixed";
                element.style.top = `${tempStickyTop}px`;
                element.style.zIndex = `${this.currentStickyZIndex}`;
                if (element.style.visibility == 'hidden') {
                    tempStickyTop += 0;
                } else {
                    tempStickyTop += DOMUtil.getComputedStyleAsNumber('height', element);
                }
            }
        });
        this.currentStickyTop = tempStickyTop;
    }

    private setOriginalStyles(state: StickyElementState): void {
        if (state.originalStyles)
            return;

        const element = state.element;
        state.originalStyles = {
            position: element.style.position,
            top: element.style.top,
            zIndex: element.style.zIndex,
            backgroundColor: element.style.backgroundColor,
            width: element.style.width,
            borderRadius: element.style.borderRadius,
            borderTopLeftRadius: element.style.borderTopLeftRadius,
            borderTopRightRadius: element.style.borderTopRightRadius,
            borderBottomLeftRadius: element.style.borderBottomLeftRadius,
            borderBottomRightRadius: element.style.borderBottomRightRadius
        };
    }

    private get scrollbarWidth(): number {
        if (!this.scrollableElement)
            return 0;
        return this.scrollableElement.offsetWidth - this.scrollableElement.clientWidth;
    }
}
